import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppStore } from '@/store'
import { isFabricEmbedMode } from '@/lib/fabricEmbedAuth'
import { isSafeAppPath } from './protocol'
import HelpAgentPanel from './HelpAgentPanel'
import TourOverlay from './TourOverlay'
// The route allowlist and the hard-exclusion list live in ./routes: the
// header launcher slot needs them too, and should not have to import the
// panel to get them.
import { isHelpAgentHardExcluded } from './routes'

/**
 * Cross-route mount for the help agent ("Akeera"). Lives in RootLayout
 * (like InAppMeetingPanel) so the widget and any running tour survive
 * navigation. Whether this person may see the help agent at all is decided
 * one level up: RootLayout mounts this only when `/v2/me/features` lists
 * `help_agent` (the per-person `hide_help_agent` flag). Here it renders
 * nothing unless ALL of these pass:
 *   1. an authenticated session,
 *   2. a non-excluded route (login, embeds, auth callbacks, standalone
 *      surfaces),
 *   3. not Fabric-embed mode (belt and braces with the /embed/* check).
 */
const HelpAgentRoot = () => {
  const userLogged = useAppStore((s) => s.userLogged)
  const helpStage = useAppStore((s) => s.helpStage)
  const helpActiveTour = useAppStore((s) => s.helpActiveTour)
  const helpPendingNavigation = useAppStore((s) => s.helpPendingNavigation)
  const consumeHelpNavigation = useAppStore((s) => s.consumeHelpNavigation)
  const location = useLocation()
  const navigate = useNavigate()

  // Act on `navigate` directives from the agent. Path safety is enforced
  // at parse time AND here (defense in depth — the slice is reachable
  // from devtools).
  useEffect(() => {
    if (!helpPendingNavigation) return
    if (isSafeAppPath(helpPendingNavigation) && helpPendingNavigation !== location.pathname) {
      navigate(helpPendingNavigation)
    }
    consumeHelpNavigation()
  }, [helpPendingNavigation, navigate, consumeHelpNavigation, location.pathname])

  if (!userLogged) return null
  if (isHelpAgentHardExcluded(location.pathname) || isFabricEmbedMode()) return null

  // An open panel or a running tour stays mounted across any
  // (non-hard-excluded) route so agent-driven navigation never unmounts a
  // streaming answer. The LAUNCHER is not here — each page header renders
  // its own HelpAgentLauncherSlot, which carries the main-page allowlist.
  return (
    <>
      {helpStage !== 'closed' && <HelpAgentPanel />}
      {helpActiveTour && <TourOverlay />}
    </>
  )
}

export default HelpAgentRoot
